import { useEffect, useState, useTransition } from 'react';
import { BrandCard } from './buds-header';
import { callApi, ShopifyAppApiError } from '@/lib/api';
import type {
  ShopifyDefaultRedemptionMode,
  ShopifyOrderKind,
  SyncDefaultsView,
} from './connection-root';

interface Props {
  initial: SyncDefaultsView;
  onChange: (next: SyncDefaultsView) => void;
  onReconnectRequired: (message?: string) => void;
}

interface SettingsResponse {
  autoSyncEnabled: boolean;
  syncDefaults: SyncDefaultsView;
}

interface CategoryOption {
  id: string;
  name: string;
}

const MODE_OPTIONS: ReadonlyArray<{
  value: ShopifyDefaultRedemptionMode;
  label: string;
  description: string;
}> = [
  {
    value: 'SHOPIFY_ORDER',
    label: 'Shopify order',
    description: 'Redemption creates an order in your Shopify shop.',
  },
  {
    value: 'SHOPIFY_VOUCHER',
    label: 'Shopify voucher',
    description: 'Redemption issues a voucher / gift card.',
  },
];

const ORDER_KIND_OPTIONS: ReadonlyArray<{
  value: ShopifyOrderKind;
  label: string;
  description: string;
}> = [
  {
    value: 'DRAFT',
    label: 'Draft order',
    description:
      'Creates a draft order for you to review and complete in Shopify admin.',
  },
  {
    value: 'FULL',
    label: 'Order',
    description:
      'Creates a paid order immediately. Inventory is decremented through your normal stock policy.',
  },
];

export function SyncDefaultsCard({
  initial,
  onChange,
  onReconnectRequired,
}: Props) {
  const [saved, setSaved] = useState<SyncDefaultsView>(initial);
  const [mode, setMode] = useState<ShopifyDefaultRedemptionMode>(
    initial.redemptionMode,
  );
  const [freeShipping, setFreeShipping] = useState(initial.freeShipping);
  const [orderKind, setOrderKind] = useState<ShopifyOrderKind>(
    initial.orderKind,
  );
  const [markup, setMarkup] = useState(String(initial.defaultMarkupPoints));
  const [categoryId, setCategoryId] = useState<string | null>(
    initial.defaultCategoryId,
  );
  const [categories, setCategories] = useState<CategoryOption[] | null>(null);
  const [categoryLoadError, setCategoryLoadError] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      try {
        const options = await callApi<CategoryOption[]>(
          '/v1/shopify-app/categories',
        );
        if (!cancelled) setCategories(options);
      } catch (err) {
        if (cancelled) return;
        const apiError = err as ShopifyAppApiError;
        if (apiError.code === 'NOT_CONNECTED') {
          onReconnectRequired(apiError.message);
          return;
        }
        setCategoryLoadError(
          `Could not load reward categories: ${apiError.message}`,
        );
      }
    }

    void loadCategories();
    return () => {
      cancelled = true;
    };
  }, [onReconnectRequired]);

  // SHOPIFY_ORDER doesn't use free shipping; SHOPIFY_VOUCHER doesn't use
  // orderKind. Tracking each separately while the user toggles between modes
  // lets us drop the value on submit without forgetting it if they switch back.
  const submittedFreeShipping =
    mode === 'SHOPIFY_VOUCHER' ? freeShipping : false;
  const submittedOrderKind = mode === 'SHOPIFY_ORDER' ? orderKind : 'DRAFT';
  const markupValid = /^\d+$/.test(markup.trim());
  const submittedMarkup = markupValid
    ? Number.parseInt(markup.trim(), 10)
    : saved.defaultMarkupPoints;
  const isDirty =
    mode !== saved.redemptionMode ||
    submittedFreeShipping !== saved.freeShipping ||
    submittedOrderKind !== saved.orderKind ||
    submittedMarkup !== saved.defaultMarkupPoints ||
    categoryId !== saved.defaultCategoryId;

  function discard() {
    setMode(saved.redemptionMode);
    setFreeShipping(saved.freeShipping);
    setOrderKind(saved.orderKind);
    setMarkup(String(saved.defaultMarkupPoints));
    setCategoryId(saved.defaultCategoryId);
    setError(null);
  }

  function save() {
    setError(null);
    const next: SyncDefaultsView = {
      redemptionMode: mode,
      freeShipping: submittedFreeShipping,
      orderKind: submittedOrderKind,
      defaultMarkupPoints: submittedMarkup,
      defaultCategoryId: categoryId,
    };
    startTransition(async () => {
      try {
        const body = await callApi<SettingsResponse>(
          '/v1/shopify-app/settings',
          { method: 'PATCH', body: { syncDefaults: next } },
        );
        setSaved(body.syncDefaults);
        setMode(body.syncDefaults.redemptionMode);
        setFreeShipping(body.syncDefaults.freeShipping);
        setOrderKind(body.syncDefaults.orderKind);
        setMarkup(String(body.syncDefaults.defaultMarkupPoints));
        setCategoryId(body.syncDefaults.defaultCategoryId);
        onChange(body.syncDefaults);
      } catch (err) {
        const e = err as ShopifyAppApiError;
        if (e.code === 'NOT_CONNECTED') {
          onReconnectRequired(e.message);
          return;
        }
        setError(`Could not update defaults: ${e.message}`);
      }
    });
  }

  return (
    <BrandCard title="Sync defaults">
      <p className="text-sm leading-relaxed text-buds-dark/80">
        Applied during sync when a product&apos;s matching metafield is missing.
      </p>

      <div className="mt-4 space-y-4">
        <fieldset>
          <legend className="text-sm font-medium text-buds-dark">
            Default redemption mode
          </legend>
          <p className="mt-1 text-xs text-buds-dark/60">
            Used when a product&apos;s{' '}
            <span className="font-mono">buds.redemption_mode</span> metafield is
            blank.
          </p>
          <div className="mt-2 space-y-2">
            {MODE_OPTIONS.map((option) => {
              const id = `default-redemption-mode-${option.value}`;
              const checked = mode === option.value;
              return (
                <label
                  key={option.value}
                  htmlFor={id}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition ${
                    checked
                      ? 'border-buds-green bg-buds-green/5'
                      : 'border-buds-dark/20 hover:border-buds-dark/40'
                  } ${pending ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <input
                    id={id}
                    type="radio"
                    name="default-redemption-mode"
                    value={option.value}
                    checked={checked}
                    disabled={pending}
                    onChange={() => setMode(option.value)}
                    className="mt-0.5 text-buds-green focus:ring-buds-green"
                  />
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-buds-dark">
                      {option.label}
                    </span>
                    <span className="text-xs text-buds-dark/60">
                      {option.description}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {mode === 'SHOPIFY_VOUCHER' && (
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-buds-dark/20 px-3 py-2">
            <input
              type="checkbox"
              checked={freeShipping}
              disabled={pending}
              onChange={(e) => setFreeShipping(e.target.checked)}
              className="mt-0.5 text-buds-green focus:ring-buds-green"
            />
            <span className="flex flex-col">
              <span className="text-sm font-medium text-buds-dark">
                Free shipping by default
              </span>
              <span className="text-xs text-buds-dark/60">
                Used when a product&apos;s{' '}
                <span className="font-mono">buds.free_shipping</span> metafield
                is blank.
              </span>
            </span>
          </label>
        )}

        {mode === 'SHOPIFY_ORDER' && (
          <fieldset>
            <legend className="text-sm font-medium text-buds-dark">
              How redemption orders are created
            </legend>
            <p className="mt-1 text-xs text-buds-dark/60">
              Choose whether redemptions create a draft for you to complete, or
              a paid order immediately.
            </p>
            <div className="mt-2 space-y-2">
              {ORDER_KIND_OPTIONS.map((option) => {
                const id = `order-kind-${option.value}`;
                const checked = orderKind === option.value;
                return (
                  <label
                    key={option.value}
                    htmlFor={id}
                    className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition ${
                      checked
                        ? 'border-buds-green bg-buds-green/5'
                        : 'border-buds-dark/20 hover:border-buds-dark/40'
                    } ${pending ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    <input
                      id={id}
                      type="radio"
                      name="order-kind"
                      value={option.value}
                      checked={checked}
                      disabled={pending}
                      onChange={() => setOrderKind(option.value)}
                      className="mt-0.5 text-buds-green focus:ring-buds-green"
                    />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-buds-dark">
                        {option.label}
                      </span>
                      <span className="text-xs text-buds-dark/60">
                        {option.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        <div>
          <label
            htmlFor="default-category"
            className="text-sm font-medium text-buds-dark"
          >
            Default category
          </label>
          <p className="mt-1 text-xs text-buds-dark/60">
            Used when a product has no non-empty{' '}
            <span className="font-mono">buds.categories</span> references.
            Product metafields always override this default.
          </p>
          <select
            id="default-category"
            value={categoryId ?? ''}
            disabled={
              pending || (categories === null && categoryLoadError === null)
            }
            onChange={(event) => setCategoryId(event.target.value || null)}
            className={`mt-2 w-full rounded-md border border-buds-dark/20 bg-white px-3 py-1.5 text-sm text-buds-dark focus:outline-none focus:ring-1 focus:ring-buds-green ${
              pending ? 'cursor-not-allowed opacity-60' : ''
            }`}
          >
            <option value="">
              {categories === null && categoryLoadError === null
                ? 'Loading categories…'
                : 'No default category'}
            </option>
            {categoryId &&
              !categories?.some((category) => category.id === categoryId) && (
                <option value={categoryId}>
                  Current category (unavailable)
                </option>
              )}
            {categories?.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-buds-dark/60">
            Syncing category metaobjects to Shopify is only needed when you want
            to choose per-product category overrides in Shopify.
          </p>
          {categoryLoadError && (
            <p className="mt-1 text-xs text-red-700">{categoryLoadError}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="default-markup-points"
            className="text-sm font-medium text-buds-dark"
          >
            Default markup (points)
          </label>
          <p className="mt-1 text-xs text-buds-dark/60">
            Added to the calculated point price of every synced product — for
            example to account for shipping costs. Products with a{' '}
            <span className="font-mono">buds.points_required</span> override are
            not affected.
          </p>
          <input
            id="default-markup-points"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={markup}
            disabled={pending}
            onChange={(e) => setMarkup(e.target.value)}
            className={`mt-2 w-32 rounded-md border px-3 py-1.5 text-sm text-buds-dark focus:outline-none focus:ring-1 focus:ring-buds-green ${
              markupValid ? 'border-buds-dark/20' : 'border-red-400'
            } ${pending ? 'cursor-not-allowed opacity-60' : ''}`}
          />
          {!markupValid && (
            <p className="mt-1 text-xs text-red-700">
              Enter a whole number of points (0 or more).
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={discard}
          disabled={pending || !isDirty}
          className="rounded-md border border-buds-dark/20 px-3 py-1.5 text-sm font-medium text-buds-dark transition hover:bg-buds-dark/5 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={save}
          disabled={pending || !isDirty || !markupValid}
          className="rounded-md bg-buds-green px-3 py-1.5 text-sm font-medium text-white transition hover:bg-buds-green/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {pending ? 'Saving…' : 'Save defaults'}
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-400/60 bg-red-50 px-3 py-2 text-xs text-red-900">
          {error}
        </div>
      )}
    </BrandCard>
  );
}
